import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { ShieldCheck, ScanSearch, CheckCircle2, Eye } from "lucide-react";

const POLL_MS = 2000;
const ANALYZE_HOLD_MS = 1500;   // only the animation is held; the timings shown are the real ones
const REMOVED = ["auto_hidden", "hidden", "deleted"];

const seconds = (from, to) => Math.max(0, Math.round((new Date(to) - new Date(from)) / 1000));
const clock = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

/**
 * Chrome-less feed for screen recordings: shows only comments that arrive while the page is open,
 * and what happened to each one. No sidebar, branding or client names.
 */
export default function Live() {
  const { scopeParam, ready } = useClientScope();
  const [items, setItems] = useState([]);
  const baseline = useRef(null);      // ids already present when the page opened
  const firstSeen = useRef({});       // id -> ms timestamp, to hold the "analyzing" beat
  const [, tick] = useState(0);

  useEffect(() => {
    if (!ready) return undefined;
    let stop = false;
    const q = `?limit=20${scopeParam ? `&${scopeParam}` : ""}`;
    const run = async () => {
      try {
        const { data } = await api.get(`/comments${q}`);
        if (stop) return;
        if (baseline.current === null) {
          baseline.current = new Set(data.map((c) => c.id));
          return;
        }
        const fresh = data.filter((c) => !baseline.current.has(c.id));
        fresh.forEach((c) => { firstSeen.current[c.id] ??= Date.now(); });
        setItems(fresh.sort((a, b) => (b.ingested_at || "").localeCompare(a.ingested_at || "")));
      } catch { /* keep the last frame on screen */ }
    };
    run();
    const t = setInterval(run, POLL_MS);
    const anim = setInterval(() => tick((n) => n + 1), 400);
    return () => { stop = true; clearInterval(t); clearInterval(anim); };
  }, [ready, scopeParam]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-10" data-testid="live-feed">
      <div className="w-full max-w-xl space-y-4">
        <div className="flex items-center gap-2 text-slate-400 text-sm font-medium">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
          </span>
          Monitoring
        </div>
        {items.map((c) => <Card key={c.id} c={c} seenAt={firstSeen.current[c.id]} />)}
      </div>
    </div>
  );
}

function Card({ c, seenAt }) {
  const decided = c.status !== "pending" && !!c.decided_at;
  const analyzing = !decided || Date.now() - (seenAt || 0) < ANALYZE_HOLD_MS;
  const removed = !analyzing && REMOVED.includes(c.status);
  const review = !analyzing && c.status === "needs_review";
  const passed = !analyzing && !removed && !review;

  const verdict = c.ai ? `${c.ai.category.replace(/_/g, " ")} · ${Math.round(c.ai.confidence * 100)}%` : "";

  return (
    <div
      className={`animate-fade-in-up bg-card rounded-2xl border shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] p-5 transition-all duration-500 ${
        removed ? "border-red-100" : "border-slate-100"
      }`}
      data-testid={`live-card-${c.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-500 shrink-0">
          {(c.author_name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">{c.author_name || "Unknown"}</p>
          <p className={`mt-0.5 text-slate-700 break-words transition-all duration-500 ${removed ? "line-through text-slate-300" : ""}`}>
            {c.text}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 min-h-[28px]">
        {analyzing && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-indigo-50 text-indigo-600 text-xs font-semibold animate-pulse whitespace-nowrap">
            <ScanSearch className="w-3.5 h-3.5" /> Analyzing…
          </span>
        )}
        {!analyzing && verdict && (
          <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold capitalize whitespace-nowrap">{verdict}</span>
        )}
        {removed && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-red-50 text-red-600 text-xs font-semibold whitespace-nowrap">
            <ShieldCheck className="w-3.5 h-3.5" /> {c.status === "deleted" ? "Deleted" : "Hidden"} on {c.platform === "instagram" ? "Instagram" : "Facebook"}
          </span>
        )}
        {review && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold whitespace-nowrap">
            <Eye className="w-3.5 h-3.5" /> Sent for review
          </span>
        )}
        {passed && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-green-50 text-green-700 text-xs font-semibold whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5" /> Passed
          </span>
        )}
      </div>

      {decided && !analyzing && c.created_at && (
        <p className="mt-3 text-xs text-slate-400 tabular-nums">
          Posted {clock(c.created_at)} → {removed ? "removed" : "checked"} {clock(c.decided_at)} ({seconds(c.created_at, c.decided_at)}s)
        </p>
      )}
    </div>
  );
}
